import { C } from "../../common/component";
import { Icon, IconHelper } from "../../common/icon-helper";
import { Requester } from "../../common/requester";
import { Txt } from "../../common/text";
import { NavigationUrlHelper } from "../../navigation/navigation-url-helper";
import { Helper } from "../../common/helper";
import { Displayer } from "../../common/displayer";
import { Form, FormDisplayer } from "../../forms/form";
import { ViewSettings } from "../../views/view-settings";
import { ItemPageData, ItemPageMainPanelData } from "../item-page";
import { MessageDisplayer } from "../../common/message-displayer";
import { ItemsMap } from "../../maps/items-map";
import { GoogleMapsModal, LocationInfo } from "../../maps/google-maps-modal";

// ReSharper disable InconsistentNaming
interface EmployeePanelData {
    Name: string;
    PermissionLevel: string;
    JobTitle: string;
    Email: string;
    Telephone: string;
    Picture: string;
}

interface ProjectPanelData {
    Location: LocationInfo;
}

// ReSharper restore InconsistentNaming

export class ItemPageMainDataPanel
{
    private data: ItemPageMainPanelData;
    private readonly urlForRefresh: string;
    private readonly refreshPage: () => void;
    private readonly container = C.div.addClass("flex");

    private projectLocation: LocationInfo;

    constructor(data: ItemPageMainPanelData, urlForRefresh: string, refreshPage: () => void)
    {
        this.data = data;
        this.urlForRefresh = urlForRefresh;
        this.refreshPage = refreshPage;
    }

    appendTo(parent)
    {
        this.container.appendTo(parent);
        this.initializeLeftPanel();
        this.initializeRightPanel();
    }

    private async refresh(): Promise<void>
    {
        this.container.empty();
        this.data = (<ItemPageData> await Requester.get(this.urlForRefresh)).MainPanelData;
        if (this.data)
        {
            this.initializeLeftPanel();
            this.initializeRightPanel();
        }
        else
        {
            NavigationUrlHelper.setSubPage(null);
            Helper.refreshPage();
        }
    }

    private initializeLeftPanel()
    {
        const leftContainer = C.column(4, false).addClass("panel panel-default").appendTo(this.container);
        const profileInfo = C.div.addClass("profile-info").appendTo(leftContainer);

        let map: ItemsMap = null;
        if (this.data.IsProjectPage)
        {
            const customData = <ProjectPanelData>this.data.CustomData;
            this.projectLocation = customData.Location;
            const mapContainer = C.div.prependTo(leftContainer);
            mapContainer.height(390);
            map = new ItemsMap();
            map.appendTo(mapContainer);
            this.refreshMap(map);
        }
        else
        {
            const customData = <EmployeePanelData>this.data.CustomData;
            C.img.addClass("img-circle img-responsive").attr("src", customData.Picture || "images/profile.png")
                .appendTo(profileInfo);

            const title = C.h4.appendTo(profileInfo);
            title.text(customData.Name);
            C.br.appendTo(title);
            C.small.text(customData.PermissionLevel).appendTo(title);
            if (customData.JobTitle)
            {
                C.label.addClass("label").text(customData.JobTitle).appendTo(profileInfo);
            }
            IconHelper.addIconWithText(C.h5.appendTo(profileInfo), Icon.Envelope, customData.Email);
            IconHelper.addIconWithText(C.h5.appendTo(profileInfo), Icon.Phone, customData.Telephone);
        }

        for (const button of this.data.Buttons)
        {
            C.getPrimaryButton(button.Name, async () =>
            {
                if (button.Type === "Location")
                {
                    this.showLocationChangeModal(button.Url, map)
                    return;
                }
                const form = new Form(button.Url);
                if (button.Type === "FormWithRefresh")
                {
                    form.onDataChanged = () => this.refresh();
                }
                await FormDisplayer.openInModal(form);
            }).appendTo(profileInfo);
        }
    }

    private refreshMap(map: ItemsMap)
    {
        map.refresh(null, this.projectLocation ? [{
            Latitude: this.projectLocation.Latitude,
            Longitude: this.projectLocation.Longitude,
            Radius: this.projectLocation.Radius }] : [],
        this.projectLocation?.Latitude ?? ItemsMap.centerLat,
        this.projectLocation?.Longitude ?? ItemsMap.centerLng,
        this.projectLocation ? 10 : ItemsMap.zoom);
    }

    private initializeRightPanel()
    {
        const rightContainer = C.column(8, false).addClass("panel panel-default").appendTo(this.container);
        const titleElement = C.h4.text(Txt.mainData).appendTo(rightContainer);
        if (this.data.SettingsUrl)
        {
            ViewSettings.addSettingsButton(titleElement, Icon.Cog, null,
                () =>
                {
                    const form = new Form(this.data.SettingsUrl);
                    form.onDataChanged = () => this.refreshPage();
                    FormDisplayer.openInModal(form);
                });
        }

        for (const property of this.data.Properties)
        {
            const col = C.div.addClass("col-md-6").appendTo(rightContainer);
            C.h5.addClass("profile-gray").text(property.Key).appendTo(col);
            Displayer.setValue(C.span.appendTo(col), property.Value);
        }
    }

    private showLocationChangeModal(changeUrl: string, displayMap: ItemsMap)
    {
        const container = document.createElement("div");
        container.className = "modal fade";
        document.body.appendChild(container);

        const modal = new GoogleMapsModal(container, this.projectLocation, true);
        modal.showModal(async (result) =>
        {

            try
            {
                const message = await Requester.post(changeUrl, result);
                this.projectLocation = result;
                this.refreshMap(displayMap);
                MessageDisplayer.showMessage(message);
            }
            catch (err)
            {
                MessageDisplayer.showErrorFromServer(err);
            }
        });
    }
}
